/**
 * Socket.io signaling (from Ratomeet)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import config from './config';

export const useSocket = () => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    socketRef.current = io(config.SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: config.RECONNECT_ATTEMPTS,
      reconnectionDelay: config.RECONNECT_DELAY,
      timeout: 10000,
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setConnectionError(null);
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });

    socketRef.current.on('connect_error', (error) => {
      setConnectionError(error.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const joinRoom = useCallback((roomId, userName) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join-room', { roomId, userName });
    }
  }, []);

  const leaveRoom = useCallback((roomId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave-room', { roomId });
    }
  }, []);

  const sendOffer = useCallback((offer, to, roomId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('offer', { offer, to, room: roomId });
    }
  }, []);

  const sendAnswer = useCallback((answer, to, roomId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('answer', { answer, to, room: roomId });
    }
  }, []);

  const sendIceCandidate = useCallback((candidate, to, roomId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('ice-candidate', { candidate, to, room: roomId });
    }
  }, []);

  const sendChatMessage = useCallback((message, roomId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat-message', { message, room: roomId });
    }
  }, []);

  const on = useCallback((event, callback) => {
    socketRef.current?.on(event, callback);
  }, []);

  const off = useCallback((event, callback) => {
    socketRef.current?.off(event, callback);
  }, []);

  return {
    isConnected,
    connectionError,
    joinRoom,
    leaveRoom,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    sendChatMessage,
    on,
    off,
  };
};

export default useSocket;
